<template>
    <div class="content-inner" tabindex="-1">
        <ComponentLoader v-if="loading"></ComponentLoader>
        <form v-if="!loading">
            <div v-if="!tfa">
                <div>
                    <label class="form-label">{{ $t("Scan this QR code with your authentication app") }}:</label>
                </div>
                <div class="pb-3 tfa-code-container">
                    <qrcode-vue :value="uri" :size="200" level="H" />
                </div>
                <div class="pb-3">
                    <label class="form-label">{{ $t("Input your one-time password") }}:</label>
                    <input
                        type="text"
                        name="tfa-token"
                        autocomplete="off"
                        v-model="token"
                        :disabled="busy"
                        maxlength="6"
                        class="form-control auto-focus"
                    />
                    <div class="form-error" v-if="errorToken">{{ errorToken }}</div>
                </div>
                <div class="pb-3">
                    <label class="form-label">{{ $t("This is the corresponding TOTP secret (for security backup)") }}:</label>
                    <input type="text" name="secret" readonly v-model="secret" :disabled="busy" maxlength="255" class="form-control" />
                </div>
                <div class="pb-3">
                    <label class="form-label">{{ $t("Input your account password to confirm this action") }}:</label>
                    <PasswordInput :disabled="busy" v-model:val="password" name="password"></PasswordInput>
                    <div class="form-error" v-if="errorPassword">{{ errorPassword }}</div>
                </div>
                <div class="pb-3">
                    <label class="form-label warning-msg">{{
                        $t("Warning: By enabling two factor authentication, all your sessions will be closed.")
                    }}</label>
                </div>
                <div class="btn-bottom-modal d-flex justify-content-end align-items-center mt-3">
                    <button :disabled="busy || !password || !token" type="button" class="btn btn-primary" @click="manageAuth()">
                        <IconItem icon="mdi:lock" /><span class="text-responsive">{{ $t("Enable two factor authentication") }}</span>
                    </button>
                    <div class="form-error" v-if="error">{{ error }}</div>
                </div>
            </div>
            <div v-else>
                <div class="pb-3">
                    <label class="form-label">{{ $t("Two factor authentication is enabled for your account") }}</label>
                </div>
                <div class="pb-3">
                    <label class="form-label">{{ $t("Input your one-time password") }}:</label>
                    <input
                        type="text"
                        name="tfa-token"
                        autocomplete="off"
                        v-model="token"
                        :disabled="busy"
                        maxlength="6"
                        class="form-control auto-focus"
                    />
                    <div class="form-error" v-if="error">{{ error }}</div>
                </div>
                <div class="btn-bottom-modal d-flex justify-content-end align-items-center mt-3">
                    <button :disabled="busy" class="btn btn-primary" type="button" @click="manageAuth()">
                        <IconItem icon="mdi:lock-open" /><span class="text-responsive">{{ $t("Disable two factor authentication") }}</span>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script lang="ts">
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";
import { getUniqueStringId } from "@/utils/unique-id";

import { AuthController } from "@/control/auth";
import { Request } from "@asanrom/request-browser";
import { Timeouts } from "@/utils/timeout";
import { ApiAccount } from "@/api/api-group-account";

import PasswordInput from "@/components/utils/PasswordInput.vue";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";

import QrcodeVue from "qrcode.vue";

export default defineComponent({
    name: "TwoFactorAuthPage",
    components: {
        PasswordInput,
        ComponentLoader,
        QrcodeVue,
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
            saveRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            loading: true,

            tfa: AuthController.RequiresTwoFactorAuthentication,

            secret: "",
            uri: "",

            token: "",
            errorToken: "",

            password: "",
            errorPassword: "",

            error: "",
            busy: false,
        };
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            if (!AuthController.isAuthenticated()) {
                this.$requireLogin();
                return;
            }

            this.tfa = AuthController.RequiresTwoFactorAuthentication;

            Request.Pending(this.loadRequestId, ApiAccount.GenerateTFA())
                .onSuccess((generatedTFA) => {
                    this.loading = false;
                    this.token = "";
                    this.secret = generatedTFA.secret;
                    this.uri = generatedTFA.uri;

                    this.$autoFocus();
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        temporalError: () => {
                            // Retry
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        manageAuth: function () {
            if (this.busy) {
                return;
            }

            this.error = "";
            this.errorPassword = "";
            this.errorToken = "";

            this.$askUserConfirmation({
                title: this.$t("Enable two factor authentication"),
                message: this.$t("This change will close all active sessions. Continue?"),
                icon: "mdi:lock-outline",
                callback: () => {
                    this.busy = true;

                    if (!this.tfa) {
                        this.enableTFA();
                    } else {
                        this.disableTFA();
                    }
                },
            });
        },

        enableTFA: function () {
            Request.Pending(this.saveRequestId, ApiAccount.SetupTFA({ secret: this.secret, password: this.password, token: this.token }))
                .onSuccess(() => {
                    this.busy = false;
                    this.password = "";
                    this.token = "";
                    this.tfa = true;

                    AuthController.CheckAuthStatus();

                    this.$showSnackBar(this.$t("Two factor authentication successfully enabled"));
                })
                .onCancel(() => {
                    this.busy = false;
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequestWrongPassword: () => {
                            this.errorPassword = this.$t("Wrong password");
                        },
                        badRequestAlready: () => {
                            this.error = this.$t("Two factor authentication is already enabled for this account");
                            AuthController.CheckAuthStatus();
                        },
                        badRequestInvalid: () => {
                            this.errorToken = this.$t("Invalid one-time password");
                        },
                        badRequest: () => {
                            this.error = this.$t("Bad request");
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.error = err.message;
                    console.error(err);
                    this.busy = false;
                });
        },

        disableTFA: function () {
            Request.Pending(this.saveRequestId, ApiAccount.RemoveTFA({ token: this.token }))
                .onSuccess(() => {
                    this.busy = false;
                    this.password = "";
                    this.token = "";
                    this.tfa = false;

                    AuthController.CheckAuthStatus();

                    this.$showSnackBar(this.$t("Two factor authentication successfully disabled"));
                })
                .onCancel(() => {
                    this.busy = false;
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequestNotEnabled: () => {
                            this.error = this.$t("Two factor authentication is not enabled for this account");
                            AuthController.CheckAuthStatus();
                        },
                        badRequestInvalid: () => {
                            this.error = this.$t("Invalid one-time password");
                        },
                        badRequest: () => {
                            this.error = this.$t("Bad request");
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.error = err.message;
                    console.error(err);
                    this.busy = false;
                });
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", this.load.bind(this));
        this.load();
    },
    beforeUnmount: function () {
        Request.Abort(this.saveRequestId);
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";

.warning-msg {
    font-size: 14px;
}
</style>
